import React, { useEffect, useState, useCallback, useRef } from 'react';
import './styles.css';
import './styles/toggle-buttons.css';
import AppHeader from './components/AppHeader';
import DemoGalleryView from './views/DemoGalleryView';
import DemoMapView from './views/DemoMapView';
import agent from './agent.js';
import BottomNavigation from './components/BottomNavigation';
import Footer from './components/Footer/Footer';
import { StyleConstants } from './StyleConstants';
import analyticsService from './services/analyticsService';

const TABLET_MAX_WIDTH = 1190;

function DemoApp() {
  const getShouldRenderMobileLayout = useCallback(() => {
    return window.innerWidth <= TABLET_MAX_WIDTH;
  }, []);

  const [shouldRenderMobileLayout, setShouldRenderMobileLayout] = useState(getShouldRenderMobileLayout());
  const [viewMode, setViewMode] = useState('gallery');
  const [galleryResetKey, setGalleryResetKey] = useState(0);
  
  const galleryScrollRef = useRef(null);

  useEffect(() => {
    const handleLayoutChange = () => {
      const newLayoutIsMobile = getShouldRenderMobileLayout();
      setShouldRenderMobileLayout(newLayoutIsMobile);
    };
    
    window.addEventListener('resize', handleLayoutChange);
    window.addEventListener('orientationchange', handleLayoutChange);
    handleLayoutChange();
    
    return () => {
      window.removeEventListener('resize', handleLayoutChange);
      window.removeEventListener('orientationchange', handleLayoutChange);
    };
  }, [viewMode, getShouldRenderMobileLayout]);

  const handleGoHome = useCallback(() => {
    // Track home navigation
    analyticsService.trackEvent({
      category: 'Demo Navigation',
      action: 'Go Home',
      label: viewMode === 'gallery' ? 'Scroll to Top' : 'Return to Gallery',
      value: 1
    });
    
    if (viewMode === 'gallery') {
      if (galleryScrollRef.current) {
        galleryScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      setGalleryResetKey(prev => prev + 1);
      return;
    }
    
    setViewMode('gallery');
  }, [viewMode]);

  useEffect(() => {
    const appElement = document.querySelector('.app');
    const setAppHeight = () => {
      if (appElement) {
        if (shouldRenderMobileLayout) {
          appElement.style.height = `${window.innerHeight}px`;
        } else {
          appElement.style.height = '100%'; 
        }
      }
    };
    setAppHeight();
    window.addEventListener('resize', setAppHeight);
    window.addEventListener('orientationchange', setAppHeight);

    return () => {
      window.removeEventListener('resize', setAppHeight);
      window.removeEventListener('orientationchange', setAppHeight);
    };
  }, [shouldRenderMobileLayout]);

  // Hide demo page from search engines
  useEffect(() => {
    // Update robots meta tag to prevent indexing
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.name = 'robots';
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.content = 'noindex, nofollow';

    // Update page title to indicate demo mode
    const originalTitle = document.title;
    document.title = 'Demo Mode - Open Houses at REMAX';

    // Cleanup on unmount
    return () => {
      robotsMeta.content = 'index, follow';
      document.title = originalTitle;
    };
  }, []);

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    
    // Track view mode changes
    analyticsService.trackEvent({
      category: 'Demo Navigation',
      action: 'View Mode Changed',
      label: mode,
      value: 1
    });
  };

  const handleBottomNavNavigate = ({ view }) => {
    setViewMode(view);
    
    // Track mobile navigation
    analyticsService.trackEvent({
      category: 'Demo Mobile Navigation',
      action: 'Bottom Nav Click',
      label: `demo-${view}`,
      value: 1
    });
  };

  const renderContent = () => {
    switch (viewMode) {
      case 'gallery':
        return (
          <>
            <div 
              ref={galleryScrollRef}
              style={{
                flexGrow: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                backgroundColor: 'var(--grey)',
                padding: `${StyleConstants.contentTopPadding} ${StyleConstants.contentSidePadding} 20px ${StyleConstants.contentSidePadding}`,
                width: '100%',
                boxSizing: 'border-box',
              }}>
              <DemoGalleryView key={galleryResetKey} isMobile={shouldRenderMobileLayout} currentView={viewMode} />
            </div>
            {!shouldRenderMobileLayout && <Footer />}
          </>
        );
      case 'map':
        return (
          <div style={{ flex: '1 1 auto', width: '100%', overflow: 'hidden', position: 'relative' }}>
            <DemoMapView isMobile={shouldRenderMobileLayout} currentView={viewMode} />
          </div>
        );
      default:
        return (
          <>
            <div 
              ref={galleryScrollRef}
              style={{
                flexGrow: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                backgroundColor: 'var(--grey)',
                padding: `${StyleConstants.contentTopPadding} ${StyleConstants.contentSidePadding} 20px ${StyleConstants.contentSidePadding}`,
                width: '100%',
                boxSizing: 'border-box',
              }}>
              <DemoGalleryView key={galleryResetKey} isMobile={shouldRenderMobileLayout} currentView={viewMode} />
            </div>
            {!shouldRenderMobileLayout && <Footer />}
          </>
        );
    }
  };

  return (
    <div className="app" style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%', 
      width: '100%',
      overflow: 'hidden'
    }}>
      <div id="app-header-wrapper" style={{ flexShrink: 0, width: '100%' }}>
        <AppHeader
          agent={agent}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          dataMode="demo"
          onDataModeChange={() => {}}
          isMobile={shouldRenderMobileLayout}
          onGoHome={handleGoHome}
        />
      </div>
      <main id="main-content-area" style={{ 
        flexGrow: 1, 
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {renderContent()}
      </main>
      {shouldRenderMobileLayout && (
        <div style={{ flexShrink: 0, width: '100%' }} id="mobile-bottom-nav-wrapper">
          <BottomNavigation
            activeView={{ dataMode: 'demo', viewMode }}
            onNavigate={handleBottomNavNavigate}
            className="mobile-visible"
          />
        </div>
      )}
    </div>
  );
}

export default DemoApp;
